import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtDate, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Package, Warehouse as WarehouseIcon, ShoppingCart, Users as UsersIcon, WarningCircle } from "@phosphor-icons/react";

const STAGES = ["new", "qualified", "proposal", "negotiation", "won", "lost"];
const STAGE_COLOR = {
  new: "lb-badge", qualified: "lb-badge-info", proposal: "lb-badge-warning",
  negotiation: "lb-badge-accent", won: "lb-badge-success", lost: "lb-badge-danger",
};

export default function Erp() {
  const [tab, setTab] = useState("inventory");
  const [summary, setSummary] = useState(null);
  const [stock, setStock] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [showWh, setShowWh] = useState(false);
  const [showStock, setShowStock] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [whForm, setWhForm] = useState({ name: "", location: "", address: "" });
  const [stockForm, setStockForm] = useState({ product_id: "", warehouse_id: "", opening_stock: 0, reorder_level: 0, cost_price: 0 });
  const [leadForm, setLeadForm] = useState({ name: "", company_name: "", email: "", phone: "", stage: "new", value: 0, source: "website" });

  const load = async () => {
    const [s, st, wh, pr, so, po, ld] = await Promise.all([
      api.get("/erp/summary"), api.get("/erp/stock"), api.get("/erp/warehouses"),
      api.get("/products"), api.get("/erp/sales-orders"),
      api.get("/erp/purchase-orders"), api.get("/erp/leads"),
    ]);
    setSummary(s.data); setStock(st.data); setWarehouses(wh.data); setProducts(pr.data);
    setSalesOrders(so.data); setPurchaseOrders(po.data); setLeads(ld.data);
  };
  useEffect(() => { load(); }, []);

  const submitWh = async (e) => {
    e.preventDefault();
    try { await api.post("/erp/warehouses", whForm); toast.success("Warehouse added"); setShowWh(false); setWhForm({ name: "", location: "", address: "" }); load(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const submitStock = async (e) => {
    e.preventDefault();
    try { await api.post("/erp/stock", { ...stockForm, opening_stock: Number(stockForm.opening_stock), reorder_level: Number(stockForm.reorder_level), cost_price: Number(stockForm.cost_price) }); toast.success("Stock item added"); setShowStock(false); load(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const submitLead = async (e) => {
    e.preventDefault();
    try { await api.post("/erp/leads", { ...leadForm, value: Number(leadForm.value) }); toast.success("Lead added"); setShowLead(false); setLeadForm({ name: "", company_name: "", email: "", phone: "", stage: "new", value: 0, source: "website" }); load(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const moveStock = async (product_id, warehouse_id, type) => {
    const qty = prompt(`${type === "in" ? "Stock in" : "Stock out"} quantity?`);
    if (!qty) return;
    try { await api.post("/erp/stock/movement", { product_id, warehouse_id, type, quantity: Number(qty) }); toast.success("Stock updated"); load(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const setStage = async (id, stage) => {
    try { await api.post(`/erp/leads/${id}/stage`, { stage }); toast.success(`Moved to ${stage}`); load(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const convertLead = async (id) => {
    try { await api.post(`/erp/leads/${id}/convert-to-customer`); toast.success("Lead converted to customer"); load(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const soToInvoice = async (id) => {
    if (!confirm("Convert this Sales Order into an Invoice?")) return;
    try {
      const { data } = await api.post(`/erp/sales-orders/${id}/to-invoice`);
      toast.success(`Invoice ${data.invoice_no} created from SO`);
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const poToBill = async (id) => {
    if (!confirm("Convert this Purchase Order into a Vendor Bill?")) return;
    try {
      const { data } = await api.post(`/erp/purchase-orders/${id}/to-bill`);
      toast.success(`Bill ${data.bill_no} created from PO`);
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  if (!summary) return <div className="text-neutral-500">Loading ERP…</div>;

  return (
    <div className="space-y-5 lb-fade" data-testid="erp-page">
      <div>
        <div className="lb-section-head">ERP</div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight">Enterprise Resource Planning</h1>
        <p className="text-neutral-600 text-sm mt-1">Inventory · Sales Orders · Purchase Orders · CRM Pipeline</p>
      </div>

      <div className="grid grid-cols-5 gap-3">
        <div className="lb-kpi lb-kpi-teal"><div className="lb-kpi-label">Stock Items</div><div className="lb-kpi-value">{summary.stock_items}</div></div>
        <div className="lb-kpi lb-kpi-outstanding"><div className="lb-kpi-label">Low Stock</div><div className="lb-kpi-value neg">{summary.low_stock_items}</div></div>
        <div className="lb-kpi lb-kpi-gst"><div className="lb-kpi-label">Open SO</div><div className="lb-kpi-value">{summary.open_sales_orders}</div></div>
        <div className="lb-kpi lb-kpi-cash"><div className="lb-kpi-label">Open PO</div><div className="lb-kpi-value">{summary.open_purchase_orders}</div></div>
        <div className="lb-kpi lb-kpi-revenue"><div className="lb-kpi-label">Pipeline Value</div><div className="lb-kpi-value mono">{fmtINR(summary.pipeline_value)}</div></div>
      </div>

      <div className="lb-card">
        <div className="flex border-b border-neutral-200">
          {[
            { k: "inventory", l: "Inventory & Stock", i: Package },
            { k: "so", l: "Sales Orders", i: ShoppingCart },
            { k: "po", l: "Purchase Orders", i: ShoppingCart },
            { k: "crm", l: "CRM Pipeline", i: UsersIcon },
          ].map((t) => {
            const Ic = t.i;
            return (
              <button key={t.k} onClick={() => setTab(t.k)}
                      className={"px-4 py-3 text-sm font-semibold border-b-2 flex items-center gap-2 " +
                        (tab === t.k ? "border-[#0A66C2] text-[#0A66C2]" : "border-transparent text-neutral-500 hover:text-[#0A66C2]")}
                      data-testid={`erp-tab-${t.k}`}>
                <Ic size={14} /> {t.l}
              </button>
            );
          })}
        </div>

        <div className="p-5">
          {tab === "inventory" && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="font-display font-bold">Stock by Warehouse</div>
                <div className="flex gap-2">
                  <button className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => setShowWh((s) => !s)} data-testid="add-warehouse-btn">
                    <WarehouseIcon size={12} /> Warehouse
                  </button>
                  <button className="lb-btn lb-btn-sm" onClick={() => setShowStock((s) => !s)} data-testid="add-stock-btn">
                    <Plus size={12} /> Stock Item
                  </button>
                </div>
              </div>

              {showWh && (
                <form onSubmit={submitWh} className="border border-neutral-300 p-4 grid grid-cols-3 gap-3 bg-neutral-50">
                  <input required className="lb-input" placeholder="Warehouse name" value={whForm.name} onChange={(e) => setWhForm({ ...whForm, name: e.target.value })} />
                  <input required className="lb-input" placeholder="Location (city)" value={whForm.location} onChange={(e) => setWhForm({ ...whForm, location: e.target.value })} />
                  <button className="lb-btn">Save Warehouse</button>
                </form>
              )}

              {showStock && (
                <form onSubmit={submitStock} className="border border-neutral-300 p-4 grid grid-cols-5 gap-3 bg-neutral-50">
                  <select required className="lb-select" value={stockForm.product_id} onChange={(e) => setStockForm({ ...stockForm, product_id: e.target.value })}>
                    <option value="">Product</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <select required className="lb-select" value={stockForm.warehouse_id} onChange={(e) => setStockForm({ ...stockForm, warehouse_id: e.target.value })}>
                    <option value="">Warehouse</option>
                    {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                  <input type="number" placeholder="Opening" className="lb-input font-mono text-right" value={stockForm.opening_stock} onChange={(e) => setStockForm({ ...stockForm, opening_stock: e.target.value })} />
                  <input type="number" placeholder="Reorder level" className="lb-input font-mono text-right" value={stockForm.reorder_level} onChange={(e) => setStockForm({ ...stockForm, reorder_level: e.target.value })} />
                  <button className="lb-btn">Save</button>
                </form>
              )}

              <table className="lb-table">
                <thead><tr><th>Product</th><th>HSN</th><th>Warehouse</th><th className="text-right">Current</th><th className="text-right">Reorder</th><th></th></tr></thead>
                <tbody>
                  {stock.map((s) => (
                    <tr key={s.id}>
                      <td className="font-semibold">{s.product_name}</td>
                      <td className="font-mono text-xs">{s.hsn}</td>
                      <td className="text-sm">{s.warehouse_name}</td>
                      <td className={"amt " + (s.low_stock ? "text-rose-600 font-bold" : "")}>
                        {s.current_stock}
                        {s.low_stock && <WarningCircle size={12} className="inline ml-1" weight="fill" />}
                      </td>
                      <td className="num">{s.reorder_level}</td>
                      <td>
                        <div className="flex gap-1">
                          <button onClick={() => moveStock(s.product_id, s.warehouse_id, "in")} className="lb-btn lb-btn-ghost lb-btn-sm">+ In</button>
                          <button onClick={() => moveStock(s.product_id, s.warehouse_id, "out")} className="lb-btn lb-btn-ghost lb-btn-sm">− Out</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!stock.length && <tr><td colSpan={6} className="text-center py-8 text-neutral-500">No stock items. Add a warehouse first, then link products.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === "so" && (
            <table className="lb-table">
              <thead><tr><th>SO #</th><th>Date</th><th>Customer</th><th className="text-right">Total</th><th>Status</th></tr></thead>
              <tbody>
                {salesOrders.map((so) => (
                  <tr key={so.id}>
                    <td className="font-mono text-xs">{so.so_no}</td>
                    <td className="text-sm">{fmtDate(so.order_date)}</td>
                    <td className="text-sm">{so.customer_snapshot?.name}</td>
                    <td className="amt">{fmtINR(so.total)}</td>
                    <td><span className={"lb-badge " + (so.status === "confirmed" ? "lb-badge-info" : "lb-badge-success")}>{so.status}</span></td>
                  </tr>
                ))}
                {!salesOrders.length && <tr><td colSpan={5} className="text-center py-8 text-neutral-500">No sales orders. Create via POST /api/erp/sales-orders</td></tr>}
              </tbody>
            </table>
          )}

          {tab === "po" && (
            <table className="lb-table">
              <thead><tr><th>PO #</th><th>Date</th><th>Vendor</th><th className="text-right">Total</th><th>Status</th><th className="text-right">Action</th></tr></thead>
              <tbody>
                {purchaseOrders.map((po) => (
                  <tr key={po.id}>
                    <td className="font-mono text-xs">{po.po_no}</td>
                    <td className="text-sm">{fmtDate(po.order_date)}</td>
                    <td className="text-sm">{po.vendor_snapshot?.name}</td>
                    <td className="amt">{fmtINR(po.total)}</td>
                    <td>
                      <span className={"lb-badge " + (po.status === "billed" ? "lb-badge-success" : "lb-badge-warning")}>{po.status}</span>
                      {po.bill_no && <span className="ml-2 font-mono text-[10px] text-neutral-500">→ {po.bill_no}</span>}
                    </td>
                    <td className="text-right">
                      {po.status !== "billed" ? (
                        <button onClick={() => poToBill(po.id)}
                                data-testid={`po-to-bill-${po.id}`}
                                className="lb-btn lb-btn-sm lb-btn-ghost">
                          Convert to Bill →
                        </button>
                      ) : (
                        <span className="text-[11px] text-neutral-400">Converted</span>
                      )}
                    </td>
                  </tr>
                ))}
                {!purchaseOrders.length && <tr><td colSpan={6} className="text-center py-8 text-neutral-500">No purchase orders yet</td></tr>}
              </tbody>
            </table>
          )}

          {tab === "crm" && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="font-display font-bold">Sales Pipeline</div>
                <button className="lb-btn lb-btn-sm" onClick={() => setShowLead((s) => !s)} data-testid="add-lead-btn">
                  <Plus size={12} /> New Lead
                </button>
              </div>

              {showLead && (
                <form onSubmit={submitLead} className="border border-neutral-300 p-4 grid grid-cols-4 gap-3 bg-neutral-50">
                  <input required className="lb-input" placeholder="Contact name" value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} data-testid="lead-name" />
                  <input className="lb-input" placeholder="Company" value={leadForm.company_name} onChange={(e) => setLeadForm({ ...leadForm, company_name: e.target.value })} />
                  <input type="email" className="lb-input" placeholder="Email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} />
                  <input type="number" step="0.01" className="lb-input font-mono text-right" placeholder="Value ₹" value={leadForm.value} onChange={(e) => setLeadForm({ ...leadForm, value: e.target.value })} />
                  <select className="lb-select" value={leadForm.source} onChange={(e) => setLeadForm({ ...leadForm, source: e.target.value })}>
                    <option value="website">Website</option><option value="referral">Referral</option>
                    <option value="ad">Ad</option><option value="cold">Cold</option>
                  </select>
                  <div className="col-span-3">
                    <button className="lb-btn">Add Lead</button>
                    <button type="button" className="lb-btn lb-btn-ghost ml-2" onClick={() => setShowLead(false)}>Cancel</button>
                  </div>
                </form>
              )}

              {/* Kanban-lite: columns per stage */}
              <div className="grid grid-cols-6 gap-2">
                {STAGES.map((s) => (
                  <div key={s} className="border border-neutral-200 bg-neutral-50 p-2 min-h-40">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-2 flex items-center justify-between">
                      <span>{s}</span>
                      <span className="lb-badge" style={{fontSize: 9}}>{summary.leads_by_stage[s] || 0}</span>
                    </div>
                    {leads.filter((l) => l.stage === s).map((l) => (
                      <div key={l.id} className="bg-white border border-neutral-200 p-2 mb-2 hover:border-[#0A66C2] transition-colors">
                        <div className="text-sm font-semibold truncate">{l.name}</div>
                        <div className="text-[11px] text-neutral-500 truncate">{l.company_name}</div>
                        <div className="text-xs font-mono mt-1">{fmtINR(l.value)}</div>
                        <div className="flex gap-1 mt-2">
                          {s !== "won" && s !== "lost" && (
                            <select value={l.stage} onChange={(e) => setStage(l.id, e.target.value)}
                                    className="text-[10px] border border-neutral-300 px-1 py-0.5 flex-1">
                              {STAGES.map((st) => <option key={st}>{st}</option>)}
                            </select>
                          )}
                          {s === "negotiation" && (
                            <button onClick={() => convertLead(l.id)} className="text-[10px] text-emerald-700 font-bold">Convert →</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
